import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { channelsHandler, handleChannelRemove, handleReallocateChannels, moveWithinList } from './helpers';

type GenericObject = Record<string, unknown>;
type EnvironmentalStressMonitoringFilters = GenericObject & {
    deviceId?: unknown;
    deviceIds?: unknown;
};

const environmentalStressMonitoringInitialState = {
    filters: {} as EnvironmentalStressMonitoringFilters,
    selectedNodes: [] as unknown[],
    route: {
        state: 'dailyData',
        url: '/environmental-stress-monitoring',
    },
    detailRoute: '/environmental-stress-monitoring/details',
    overview: {
        title: '',
        status: {
            label: '',
            severity: 'normal',
        },
        metrics: [] as unknown[],
    },
    secondary: [] as unknown[],
    statusCount: {
        warnings: 0,
    },
    maxCount: 6,
    viewAllButton: false,
    viewTitleBar: true,
    countMode: 'custom',
    deviceId: undefined as string | undefined,
};

type EnvironmentalStressMonitoringState = typeof environmentalStressMonitoringInitialState;
type EnvironmentalStressMonitoringActionPayload = GenericObject & {
    key?: string;
    value?: unknown;
};
type EnvironmentalStressMonitoringKeyValuePayload = { key: string; value: unknown };
type EnvironmentalStressMonitoringSelectedNode = { id: string } & GenericObject;
type EnvironmentalStressMonitoringSelectedNodesPayload = {
    value: EnvironmentalStressMonitoringSelectedNode | EnvironmentalStressMonitoringSelectedNode[] | null;
};
type EnvironmentalStressMonitoringDeviceLinkPayload = {
    reset?: boolean;
    data?: { id?: string };
};

const toObject = (value: unknown): GenericObject =>
    value && typeof value === 'object' ? ({ ...value } as GenericObject) : {};

const hasItems = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

const shouldMigrateDeviceId = (filters: EnvironmentalStressMonitoringFilters): boolean =>
    hasItems(filters.deviceId) && !hasItems(filters.deviceIds);

const getNotificationCounts = (payload: GenericObject, secondary: unknown[]): { alarms: number; warnings: number } => {
    const statusCount = toObject(payload.statusCount);
    const notifications = toObject(payload.notifications);
    const alarms = statusCount.alarms ?? notifications.alarmCount;
    const warnings = statusCount.warnings ?? notifications.warningCount;

    return {
        alarms: Number.isFinite(Number(alarms))
            ? Number(alarms)
            : secondary.filter((item) => toObject(item).type === 'alarm').length,
        warnings: Number.isFinite(Number(warnings))
            ? Number(warnings)
            : secondary.filter((item) => toObject(item).type === 'warning').length,
    };
};

const normalizeEnvironmentalStressMonitoringPayload = (payload: unknown): Record<string, unknown> => {
    const normalizedPayload = toObject(payload);
    const filters = toObject(normalizedPayload.filters) as EnvironmentalStressMonitoringFilters;

    if (shouldMigrateDeviceId(filters)) {
        const nextFilters: EnvironmentalStressMonitoringFilters = {
            ...filters,
            deviceIds: filters.deviceId,
        };
        delete nextFilters.deviceId;
        normalizedPayload.filters = nextFilters;
    }

    return normalizedPayload;
};

const environmentalStressMonitoringWidgetSlice = createSlice({
    name: 'environmental_stress_monitoring',
    initialState: environmentalStressMonitoringInitialState,
    reducers: {
        resetEnvironmentalStressMonitoringConfiguration: () => environmentalStressMonitoringInitialState,
        setPredefinedEnvironmentalStressMonitoring: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<GenericObject>
        ) => {
            const normalizedPayload = normalizeEnvironmentalStressMonitoringPayload(action.payload);

            Object.keys(normalizedPayload).forEach((key: string) => {
                const stateKey = key as keyof EnvironmentalStressMonitoringState;
                state[stateKey] = normalizedPayload[key] as never;
            });
        },
        setEnvironmentalStressMonitoringDevice: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringActionPayload>
        ) => {
            channelsHandler(state, action);
        },
        removeEnvironmentalStressMonitoringNode: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringActionPayload>
        ) => {
            handleChannelRemove(state, action, 'id');
        },
        setEnvironmentalStressMonitoringFilters: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringFilters>
        ) => {
            state.filters = action.payload;
        },
        setEnvironmentalStressMonitoringSelectedNodes: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringSelectedNodesPayload>
        ) => {
            state.selectedNodes = (
                Array.isArray(action.payload.value)
                    ? action.payload.value.slice(0, 1)
                    : action.payload.value
                    ? [action.payload.value]
                    : []
            ) as never;
        },
        setEnvironmentalStressMonitoringData: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<GenericObject | unknown[]>
        ) => {
            if (Array.isArray(action.payload)) {
                state.secondary = action.payload;
                return;
            }

            const payload = action.payload ?? {};
            if (payload.route && typeof payload.route === 'object') {
                state.route = payload.route as EnvironmentalStressMonitoringState['route'];
            }
            if (typeof payload.detailRoute === 'string') {
                state.detailRoute = payload.detailRoute;
            }
            if (payload.overview && typeof payload.overview === 'object') {
                state.overview = payload.overview as EnvironmentalStressMonitoringState['overview'];
            }
            const notifications = toObject(payload.notifications);
            state.secondary = Array.isArray(payload.secondary)
                ? payload.secondary
                : Array.isArray(payload.records)
                ? payload.records
                : Array.isArray(notifications.items)
                ? notifications.items
                : [];

            state.statusCount = getNotificationCounts(payload, state.secondary);
        },
        setEnvironmentalStressMonitoringPreferences: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringKeyValuePayload>
        ) => {
            const stateKey = action.payload.key as keyof EnvironmentalStressMonitoringState;
            state[stateKey] = action.payload.value as never;
        },
        reallocateEnvironmentalStressMonitoringFilters: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringActionPayload>
        ) => {
            handleReallocateChannels(state, action);
        },
        moveBetweenEnvironmentalStressMonitoring: (
            state: EnvironmentalStressMonitoringState,
            action: PayloadAction<EnvironmentalStressMonitoringActionPayload>
        ) => {
            moveWithinList(state, action);
        },
    },
    extraReducers: (builder) => {
        builder.addCase('widgetConfiguration/toggleDeviceLinkMode', (state: EnvironmentalStressMonitoringState) => {
            state.secondary = [];
            state.selectedNodes = [];
            state.filters = {};
        });
        builder.addCase('widgetConfiguration/setWidgetDevice', (state: EnvironmentalStressMonitoringState, action) => {
            const payload = action as PayloadAction<EnvironmentalStressMonitoringDeviceLinkPayload>;
            if (payload.payload.reset) {
                state.secondary = [];
                state.overview = environmentalStressMonitoringInitialState.overview;
            }
            state.deviceId = payload.payload.data?.id;
        });
    },
});

export const {
    setEnvironmentalStressMonitoringDevice,
    removeEnvironmentalStressMonitoringNode,
    setEnvironmentalStressMonitoringData,
    moveBetweenEnvironmentalStressMonitoring,
    setEnvironmentalStressMonitoringPreferences,
    setEnvironmentalStressMonitoringFilters,
    setEnvironmentalStressMonitoringSelectedNodes,
    resetEnvironmentalStressMonitoringConfiguration,
    setPredefinedEnvironmentalStressMonitoring,
    reallocateEnvironmentalStressMonitoringFilters,
} = environmentalStressMonitoringWidgetSlice.actions;

export default environmentalStressMonitoringWidgetSlice;
